import React from 'react'
import PanelContainer from './PanelContainer'
import { LazyDropOffSchedule, WasteType } from '../../core/domain/models'
import LeftArrowIcon from '../icons/LeftArrowIcon'
import styled from 'styled-components'
import { EarthIcon, PhoneIcon } from '../icons'
import RewardButton from '../dropOffLocationDetails/RewardButton'
import GpsButton from '../dropOffLocationDetails/GpsButton'
import DropOffScheduleDropdown from '../dropOffLocationDetails/DropOffScheduleDropdown'
import { useConfigurationContext, useDropOffLocationContext } from '../../core/application/contexts'
import WasteTypeRow from '../shared/WasteTypeRow'
import { getClickableLink } from '../../core/application/utils/linkUtils'


export interface IDropOffDetailPanelProps {
  dropOffLocation: {
    id: string
    name: string
    types: string[]
    address?: {
      line: string
      zipCode: string
      province?: string
      countryCode: string
      city: string
    }
    coordinate: {
      latitude: number
      longitude: number
    }
    pictures: Array<string>
    availableWasteTypes: Array<string>
    schedule?: LazyDropOffSchedule
    phone?: string
    website?: string
    isOpenNow: boolean
  }
  onReturn: () => void
}

const DropOffDetailPanel: React.FC<IDropOffDetailPanelProps> = ({ dropOffLocation, onReturn }) => {

  const { name, address, pictures, availableWasteTypes, phone, website, coordinate, schedule, isOpenNow } = dropOffLocation
  const { wasteTypeList } = useConfigurationContext()
  const { setDisplayGetRewardDropoffId } = useDropOffLocationContext()

  const wastes: Array<WasteType> = wasteTypeList.filter((wasteType: WasteType) => availableWasteTypes.includes(wasteType.id))

  const firstPicture = pictures.length > 0 ? pictures[0] : undefined
  const hasReward = dropOffLocation.types.includes('reward')

  let cityText = ''
  if (address?.zipCode) {
    cityText += address.zipCode
    cityText += ' '
  }
  if (address?.city) {
    cityText += address.city
  }

  return (
    <Container data-testid='dropOffDetailPanel-container'>
      <GoBackWrapper data-testid='goBack-button' onClick={onReturn}>
        <LeftArrowIcon />
      </GoBackWrapper>
      <Wrapper>
        <InfoSection>
          {firstPicture && <PictureWrapper data-testid='picture-wrapper' image={firstPicture} />}
          <InfoWrapper>
            <div className='name' data-testid='name'>
              {name}
            </div>
            {address && <div className='address' data-testid='address'>
              <div>{address.line}</div>
              {address.province && <div>{address.province}</div>}
              <div>
                <span>{cityText}</span>
              </div>
            </div>}
          </InfoWrapper>
        </InfoSection>
        {wastes.length > 0 && (
          <WasteInformationRow>
            <WasteTypeRow wasteTypes={wastes} withShowAllButton={false} onShowAll={undefined}/>
          </WasteInformationRow>
        )}
        {schedule && <DropOffScheduleDropdown schedule={schedule} isOpenNow={isOpenNow}/>}
        {phone && (
          <InformationRow data-testid='phone-wrapper'>
            <a href={`tel:${phone}`} target='_blank' rel='noreferrer'>
              <PhoneIcon />
              <span>{phone}</span>
            </a>
          </InformationRow>
        )}
        {website && (
          <InformationRow data-testid='website-wrapper'>
            <a href={getClickableLink(website)} target='_blank' rel='noreferrer'>
              <EarthIcon />
              <span>{website}</span>
            </a>
          </InformationRow>
        )}
      </Wrapper>
      <BottomSection>
        {hasReward && <RewardButton onClick={() => setDisplayGetRewardDropoffId(dropOffLocation.id)}/>}
        <GpsButton coordinate={coordinate} />
      </BottomSection>
    </Container>
  )
}

const Container = styled(PanelContainer)`
  padding: 16px;
`

const GoBackWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-size: ${({ theme }) => theme.size.normal};
  color: ${({ theme }) => theme.color.black};
  column-gap: 8px;
  cursor: pointer;
  margin-bottom: 12px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  margin-bottom: 25px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
`

interface IPictureWrapperProps {
  image: string
}

const PictureWrapper = styled.div<IPictureWrapperProps>`
  display: flex;
  flex-direction: column;
  height: 64px;
  min-height: 64px;
  width: 96px;
  min-width: 96px;
  background-image: ${({ image }) => `url(${image})`};
  background-size: cover;
  border-radius: 4px;
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  .name {
    font-family: ${({ theme }) => theme.font.semi};
    font-size: ${({ theme }) => theme.size.small};
  }

  .address {
    font-size: ${({ theme }) => theme.size.smaller};
    display: flex;
    flex-direction: column;
  }
`

const WasteInformationRow = styled.div`
  display: flex;
  flex-direction: column;

  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.lightGrey};
`

const InformationRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  justify-content: flex-start;
  align-items: center;
  font-size: ${({ theme }) => theme.size.normal};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.color.lightGrey};
    padding: 16px 0;
  }

  padding: 16px 0 0 0;

  .column {
    display: flex;
    flex-direction: column;
  }

  a {
    cursor: pointer;
    color: ${({ theme }) => theme.color.black};
    text-decoration: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    column-gap: 5px;
  }
`

const BottomSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  column-gap: 8px;
  padding: 0 16px;
`


export default DropOffDetailPanel
